import { SHIPPING_ADDRESS_MAX_LENGTHS, type ShippingAddress } from '@repo/api-contracts';
import { FormField } from '@repo/ui';
import type { ReactElement } from 'react';
import { PostcodeSearchFields } from './postcode-search-fields';

export interface ShippingAddressFieldsProps {
  readonly value: ShippingAddress;
  /** 바뀐 칸만 넘긴다 — 호출부가 이전 값과 병합한다. */
  readonly onChange: (patch: Partial<ShippingAddress>) => void;
  readonly errors?: Partial<Record<keyof ShippingAddress, string>>;
  /** 저장 중 등 — 모든 칸과 주소 검색을 잠근다. */
  readonly disabled?: boolean;
}

/** 칸 높이 44px — 휴대폰에서도 누르기 쉬운 크기. */
const INPUT_CLASS = 'h-11';

/**
 * 배송지 입력 — 견적 요청 폼(견적 spec §5-4)·주문하기 배송지(주문 spec §8-1)·주문 배송지 변경(§8-3)이 함께 쓰는 하나의
 * 컴포넌트다(R14, 주문 spec §12 "같은 컴포넌트"). 칸 상한은 배송지 단일 계약(`SHIPPING_ADDRESS_MAX_LENGTHS`)과 같다.
 * 우편번호·기본 주소는 주소 검색 두 칸(`PostcodeSearchFields` — 비회원 견적 배송지 카드와 같다)으로 채우고 상세 주소는 직접 적는다.
 */
export const ShippingAddressFields = ({
  value,
  onChange,
  errors = {},
  disabled = false,
}: ShippingAddressFieldsProps): ReactElement => (
  // 칸 묶음 이름(배송지)은 낭독기에만 둔다 — 보이는 제목은 감싸는 화면(견적 요청·주문하기 묶음 제목, 배송지 변경 창 제목)이 단다.
  <fieldset disabled={disabled} className="grid min-w-0 gap-5 sm:grid-cols-2">
    <legend className="sr-only">배송지</legend>

    <FormField
      label="수령인 이름"
      autoComplete="name"
      isRequired
      required
      maxLength={SHIPPING_ADDRESS_MAX_LENGTHS.recipientName}
      className={INPUT_CLASS}
      value={value.recipientName}
      error={errors.recipientName}
      onChange={(event) => onChange({ recipientName: event.target.value })}
    />

    <FormField
      label="수령인 연락처"
      type="tel"
      autoComplete="tel"
      isRequired
      required
      maxLength={SHIPPING_ADDRESS_MAX_LENGTHS.recipientPhone}
      className={INPUT_CLASS}
      value={value.recipientPhone}
      error={errors.recipientPhone}
      onChange={(event) => onChange({ recipientPhone: event.target.value })}
    />

    <PostcodeSearchFields
      value={{ postalCode: value.postalCode, address1: value.address1 }}
      errors={{ postalCode: errors.postalCode, address1: errors.address1 }}
      onChange={onChange}
    />

    <FormField
      label="상세 주소"
      autoComplete="address-line2"
      isRequired
      required
      maxLength={SHIPPING_ADDRESS_MAX_LENGTHS.address2}
      className={INPUT_CLASS}
      containerClassName="sm:col-span-2"
      value={value.address2}
      error={errors.address2}
      onChange={(event) => onChange({ address2: event.target.value })}
    />
  </fieldset>
);
